import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link } from 'react-router-dom';
import { useSEO } from '../../hooks/useSEO';

const EnvironmentalImpactDigitalCards = () => {
  useSEO({
    title: "Environmental Impact of Digital Business Cards | AM CARDMATE Blog",
    description: "Learn how digital business cards help reduce paper waste and environmental impact while providing superior networking capabilities.",
    image: "/assets/environment.jpg",
    keywords: "environmental impact, sustainable business cards, eco-friendly, paperless, green business"
  });

  useEffect(() => {
    const schemas = [
      {
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        "headline": "The Environmental Impact of Digital Business Cards",
        "description": "Learn how digital business cards help reduce environmental impact",
        "datePublished": "2025-06-03",
        "dateModified": "2025-06-03",
        "author": { "@type": "Organization", "name": "AM CARDMATE Green Team" },
        "publisher": { "@type": "Organization", "name": "AM CARDMATE", "logo": { "@type": "ImageObject", "url": "https://amcardmate.com/assets/logo.svg" } },
        "image": "https://amcardmate.com/assets/environment.jpg",
        "mainEntityOfPage": { "@type": "WebPage", "@id": "https://amcardmate.com/blog/environmental-impact-digital-cards" }
      }
    ];
    schemas.forEach(schema => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    });
  }, []);

  const [treesCounter, setTreesCounter] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setTreesCounter(prev => prev < 88000 ? prev + 1000 : 88000);
    }, 50);
    return () => clearInterval(interval);
  }, []);

  return (
    <>
      {/* Hero Section */}
      <section className="pt-32 md:pt-36 lg:pt-40 pb-20 bg-gradient-to-b from-white via-green-50/30 to-white relative overflow-hidden">
        <div className="absolute top-0 left-0 w-[800px] h-[800px] bg-gradient-to-br from-green-200/20 to-emerald-200/20 rounded-full filter blur-3xl" />
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-4xl mx-auto">
            {/* Breadcrumb */}
            <nav className="flex flex-wrap items-center gap-2 text-caption mb-6">
              <Link to="/" className="hover:text-primary-blue transition-colors">Home</Link>
              <FontAwesomeIcon icon="chevron-right" className="text-xs" />
              <Link to="/blog" className="hover:text-primary-blue transition-colors">Blog</Link>
              <FontAwesomeIcon icon="chevron-right" className="text-xs" />
              <span className="text-primary-blue">Environmental Impact</span>
            </nav>

            <div className="text-center mb-8">
              <div className="badge badge-green inline-flex mb-6">
                <FontAwesomeIcon icon="leaf" />
                <span>Sustainability</span>
              </div>

              <h1 className="text-h1 mb-6">
                The Environmental Impact of
                <span className="block text-gradient-green mt-2">Digital Business Cards</span>
              </h1>

              <div className="flex flex-wrap items-center justify-center gap-4 md:gap-6 text-caption">
                <span className="flex items-center gap-2">
                  <FontAwesomeIcon icon="user" className="text-primary-blue" />
                  Green Team
                </span>
                <span className="flex items-center gap-2">
                  <FontAwesomeIcon icon="calendar" className="text-primary-blue" />
                  June 3, 2025
                </span>
                <span className="flex items-center gap-2">
                  <FontAwesomeIcon icon="clock" className="text-primary-blue" />
                  3 min read
                </span>
              </div>
            </div>

            {/* Hero Image with Counter */}
            <div className="relative rounded-3xl overflow-hidden shadow-2xl mb-12">
              <img src="https://images.unsplash.com/photo-1542601906990-b4d3fb778b09?w=1200&h=600&fit=crop" alt="Forest conservation and sustainability" className="w-full h-auto" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
              <div className="absolute bottom-4 md:bottom-8 left-4 md:left-8 right-4 md:right-8 bg-white/90 backdrop-blur-sm rounded-2xl p-4 md:p-6">
                <div className="text-center">
                  <div className="text-h2 font-bold text-green-600 mb-2">{treesCounter.toLocaleString()}</div>
                  <div className="text-body text-gray-600">Trees saved annually by digital business cards</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-4xl mx-auto">
            {/* Intro */}
            <p className="text-body-lg text-gray-600 mb-8">
              Let's talk about the elephant in the room – or should we say, the forest of trees in the printer? Every year, billions of business cards are printed worldwide, and here's the kicker: 88% of them end up in the trash within a week. That's not just wasteful; it's an environmental disaster happening in slow motion. 🌍
            </p>

            <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-6 md:p-8 mb-12">
              <h3 className="text-h4 text-dark mb-4 flex items-center gap-3">
                <FontAwesomeIcon icon="exclamation-circle" className="text-green-600" />
                The Shocking Truth
              </h3>
              <p className="text-body text-gray-600">
                <strong>10 billion</strong> business cards are printed annually worldwide. That's enough cards to circle the Earth 39 times! And most of them? They're collecting dust in drawers or decomposing in landfills. Time for a change, don't you think?
              </p>
            </div>

            {/* Paper Trail */}
            <div className="mb-16">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 bg-gradient-to-br from-red-400 to-red-600 rounded-xl flex items-center justify-center text-white">
                  <FontAwesomeIcon icon="tree" />
                </div>
                <h2 className="text-h3 text-dark">The Paper Trail of Destruction</h2>
              </div>

              <div className="bg-gray-50 rounded-2xl p-6 md:p-8 mb-8">
                <h3 className="text-card-title text-dark mb-4">Breaking Down the Numbers</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  {[
                    { value: '7.2 million', label: 'Trees cut down annually for business cards', color: 'text-red-600' },
                    { value: '4 billion', label: 'Gallons of water used in production', color: 'text-blue-600' },
                    { value: '1.6 billion', label: 'Pounds of waste generated', color: 'text-purple-600' },
                    { value: '88%', label: 'Cards thrown away within a week', color: 'text-orange-600' }
                  ].map((stat, i) => (
                    <div key={i} className="bg-white rounded-2xl p-6 shadow-md hover:shadow-xl transition-all duration-300">
                      <div className={`text-h3 font-bold ${stat.color} mb-2`}>{stat.value}</div>
                      <div className="text-body-sm text-gray-600">{stat.label}</div>
                    </div>
                  ))}
                </div>
              </div>

              <p className="text-body text-gray-600 mb-6">
                But wait, it gets worse! The environmental impact doesn't stop at cutting down trees. Let's follow the journey of a traditional business card:
              </p>

              <div className="space-y-4">
                {[
                  { step: 1, title: "Tree Harvesting", impact: "Deforestation and habitat loss", icon: "tree", gradient: "from-green-400 to-green-600" },
                  { step: 2, title: "Pulp Production", impact: "Chemical processing and water pollution", icon: "flask", gradient: "from-blue-400 to-blue-600" },
                  { step: 3, title: "Printing Process", impact: "Toxic inks and energy consumption", icon: "print", gradient: "from-purple-400 to-purple-600" },
                  { step: 4, title: "Transportation", impact: "Carbon emissions from shipping", icon: "truck", gradient: "from-orange-400 to-orange-600" },
                  { step: 5, title: "Disposal", impact: "Landfill waste or incineration", icon: "trash", gradient: "from-red-400 to-red-600" }
                ].map((item) => (
                  <div key={item.step} className="flex items-start gap-4 bg-white rounded-xl p-4 shadow-sm hover:shadow-md transition-all">
                    <div className={`w-12 h-12 bg-gradient-to-br ${item.gradient} rounded-full flex items-center justify-center text-white flex-shrink-0`}>
                      <FontAwesomeIcon icon={item.icon} />
                    </div>
                    <div>
                      <h4 className="text-body font-bold text-dark">Step {item.step}: {item.title}</h4>
                      <p className="text-body-sm text-gray-600">{item.impact}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Digital Solution - Full width banner */}
      <section className="py-16 bg-gradient-to-r from-[#4DA2FF] to-[#DE6161] relative overflow-hidden">
        <div className="absolute inset-0 bg-dots-pattern opacity-10"></div>
        <div className="container mx-auto px-4 sm:px-6 relative z-10">
          <div className="text-center mb-8">
            <div className="text-h1 font-bold text-white mb-2">1 Digital Card = ∞ Connections</div>
            <div className="text-body-lg text-white/90">Zero environmental impact, infinite possibilities</div>
          </div>
          <div className="grid grid-cols-3 gap-6 max-w-2xl mx-auto">
            {[
              { icon: 'tree', title: '0 Trees Cut', subtitle: 'Forever' },
              { icon: 'water', title: '0 Water Wasted', subtitle: 'Not a drop' },
              { icon: 'recycle', title: '0 Waste Created', subtitle: 'Completely clean' }
            ].map((item, i) => (
              <div key={i} className="text-center text-white">
                <FontAwesomeIcon icon={item.icon} className="text-3xl md:text-4xl mb-3" />
                <div className="text-body font-bold">{item.title}</div>
                <div className="text-body-sm opacity-80">{item.subtitle}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits & Ripple Effect */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-white to-green-50/30">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-4xl mx-auto">
            {/* Benefits */}
            <div className="mb-16">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 bg-gradient-to-br from-green-400 to-emerald-600 rounded-xl flex items-center justify-center text-white">
                  <FontAwesomeIcon icon="seedling" />
                </div>
                <h2 className="text-h3 text-dark">The Digital Solution</h2>
              </div>

              <p className="text-body text-gray-600 mb-8">
                Here's where digital business cards become the superhero of sustainability. With AM CARDMATE, you're not just saving trees – you're revolutionizing how we think about professional networking:
              </p>

              <div className="space-y-6">
                {[
                  { title: 'One Card, Lifetime Use', desc: "Update your information instantly without reprinting. Changed jobs? New phone number? Update once, and everyone with your card sees the new info. Magic!" },
                  { title: 'Zero Physical Waste', desc: 'No more boxes of outdated cards in your drawer. No more cards in landfills. Just pure, clean, digital efficiency.' },
                  { title: 'Carbon Neutral Networking', desc: 'No transportation, no manufacturing, no waste disposal. Your carbon footprint for networking? Practically zero!' }
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-4">
                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <FontAwesomeIcon icon="check" className="text-green-600 text-sm" />
                    </div>
                    <div>
                      <h4 className="text-card-title text-dark mb-2">{item.title}</h4>
                      <p className="text-card-body">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Ripple Effect */}
            <div className="mb-16">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-cyan-600 rounded-xl flex items-center justify-center text-white">
                  <FontAwesomeIcon icon="water" />
                </div>
                <h2 className="text-h3 text-dark">The Ripple Effect: Beyond Trees</h2>
              </div>

              <p className="text-body text-gray-600 mb-8">
                When you choose digital business cards, you're not just saving trees. You're part of a larger movement that impacts:
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {[
                  { icon: 'globe', title: 'Climate Change', desc: 'Fewer trees cut = more CO₂ absorbed. Every tree saved continues to fight climate change for decades.', gradient: 'from-blue-400 to-cyan-600', bg: 'from-blue-50 to-cyan-50' },
                  { icon: 'paw', title: 'Wildlife Habitats', desc: "Forests aren't just trees – they're homes. Protecting forests means protecting countless species.", gradient: 'from-green-400 to-emerald-600', bg: 'from-green-50 to-emerald-50' },
                  { icon: 'tint', title: 'Water Conservation', desc: 'Paper production is water-intensive. Digital cards save millions of gallons annually.', gradient: 'from-purple-400 to-pink-600', bg: 'from-purple-50 to-pink-50' },
                  { icon: 'wind', title: 'Air Quality', desc: "Less manufacturing = less pollution. Breathe easier knowing you're part of the solution.", gradient: 'from-orange-400 to-red-600', bg: 'from-orange-50 to-red-50' }
                ].map((item, i) => (
                  <div key={i} className={`bg-gradient-to-br ${item.bg} rounded-2xl p-6`}>
                    <div className="flex items-center gap-3 mb-3">
                      <div className={`w-10 h-10 bg-gradient-to-br ${item.gradient} rounded-xl flex items-center justify-center text-white`}>
                        <FontAwesomeIcon icon={item.icon} />
                      </div>
                      <h3 className="text-card-title text-dark">{item.title}</h3>
                    </div>
                    <p className="text-card-body">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Personal Eco Impact */}
            <div className="mb-16">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 bg-gradient-to-br from-green-400 to-emerald-600 rounded-xl flex items-center justify-center text-white">
                  <FontAwesomeIcon icon="calculator" />
                </div>
                <h2 className="text-h3 text-dark">Your Personal Eco-Impact</h2>
              </div>

              <div className="bg-white rounded-3xl shadow-xl p-6 md:p-8">
                <p className="text-body text-gray-600 mb-8">
                  The average professional gives out 200 business cards per year. By switching to AM CARDMATE, here's what YOU save annually:
                </p>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                  {[
                    { icon: 'tree', value: '0.16', label: 'Trees Saved', gradient: 'from-green-400 to-emerald-600' },
                    { icon: 'tint', value: '80', label: 'Gallons of Water', gradient: 'from-blue-400 to-cyan-600' },
                    { icon: 'bolt', value: '4.8', label: 'kWh Energy', gradient: 'from-purple-400 to-pink-600' },
                    { icon: 'leaf', value: '3.2', label: 'lbs of CO₂', gradient: 'from-orange-400 to-red-600' }
                  ].map((item, i) => (
                    <div key={i} className="text-center">
                      <div className={`w-16 h-16 md:w-20 md:h-20 bg-gradient-to-br ${item.gradient} rounded-full flex items-center justify-center text-white text-body-lg mx-auto mb-3`}>
                        <FontAwesomeIcon icon={item.icon} />
                      </div>
                      <div className="text-h4 font-bold text-dark">{item.value}</div>
                      <div className="text-body-sm text-gray-600">{item.label}</div>
                    </div>
                  ))}
                </div>

                <div className="mt-8 p-6 bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl">
                  <p className="text-center text-body-sm text-gray-600">
                    <strong>Over 10 years:</strong> You'll save 1.6 trees, 800 gallons of water, and prevent 32 pounds of CO₂ emissions. That's like planting your own mini-forest! 🌲
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-r from-[#4DA2FF] to-[#DE6161] relative overflow-hidden">
        <div className="absolute inset-0 bg-dots-pattern opacity-10"></div>
        <div className="absolute top-0 left-0 w-96 h-96 bg-white/10 rounded-full filter blur-3xl" />
        <div className="container mx-auto px-4 sm:px-6 relative z-10">
          <div className="text-center">
            <h2 className="text-h2 text-white mb-4">Join the Green Revolution</h2>
            <p className="text-body-lg text-white/90 mb-8 max-w-2xl mx-auto">
              Every AM CARDMATE user is an environmental warrior. Together, we're not just networking – we're saving the planet, one tap at a time.
            </p>
            <div className="grid grid-cols-3 gap-6 max-w-2xl mx-auto mb-8">
              {[
                { value: '50,000+', label: 'Trees Saved So Far' },
                { value: '2M+', label: 'Gallons Conserved' },
                { value: '500 tons', label: 'CO₂ Prevented' }
              ].map((stat, i) => (
                <div key={i} className="text-center text-white">
                  <div className="text-h3 font-bold mb-2">{stat.value}</div>
                  <div className="text-body-sm text-white/80">{stat.label}</div>
                </div>
              ))}
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/personal-card" className="btn bg-white text-[#4DA2FF] hover:bg-gray-100 transition-all shadow-lg">
                <FontAwesomeIcon icon="leaf" />
                <span>Go Green Today</span>
              </Link>
              <Link to="/nfc-cards" className="btn bg-white/20 text-white hover:bg-white/30 transition-all">
                <FontAwesomeIcon icon="shopping-cart" />
                <span>Get Your Eco Card</span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Author & Related */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-white to-green-50/30">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-4xl mx-auto">
            <div className="border-t pt-8 mb-12">
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-green-400 to-emerald-600 flex items-center justify-center text-white font-bold text-body-lg flex-shrink-0">GT</div>
                <div>
                  <h3 className="text-card-title text-dark">Green Team</h3>
                  <p className="text-body-sm text-gray-500 mb-2">AM CARDMATE Sustainability Department</p>
                  <p className="text-body-sm text-gray-600">
                    We're a passionate group of tree-huggers, ocean-lovers, and tech enthusiasts working to make professional networking sustainable.
                  </p>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-h3 text-dark mb-6">Continue Your Green Journey</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                {[
                  { to: '/blog/future-of-networking', title: 'The Future of Networking', gradient: 'from-purple-500 to-pink-500' },
                  { to: '/blog/creative-ways-use-nfc-card', title: '10 Creative Ways to Use Your NFC Card', gradient: 'from-orange-400 to-red-600' },
                  { to: '/blog/what-is-nfc-technology', title: 'What is NFC Technology?', gradient: 'from-blue-400 to-blue-600' }
                ].map((post, i) => (
                  <Link key={i} to={post.to} className={`group ${i === 2 ? 'sm:col-span-2 md:col-span-1' : ''}`}>
                    <div className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300">
                      <div className={`h-40 bg-gradient-to-br ${post.gradient}`}></div>
                      <div className="p-4">
                        <h4 className="text-body font-bold text-dark group-hover:text-primary-blue transition-colors">{post.title}</h4>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default EnvironmentalImpactDigitalCards;